type DateRange = {
  from: string; // YYYY-MM from the ISO 8601 date format
  to: string; // YYYY-MM from the ISO 8601 date format
}

const mergeOverlappingDateRanges = (dateRanges: DateRange[]): DateRange[] => {
  const sorted = dateRanges.sort(
    // By start, ascending
    (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime(),
  );

  const ret = sorted.reduce((acc, curr) => {
    // Skip the first range
    if (acc.length === 0) {
      return [curr];
    }

    const prev = acc.pop();

    if (!prev) {
      return [curr];
    }

    if (curr.to <= prev.to) {
      // Current range is completely inside previous
      return [...acc, prev];
    }

    // Merges overlapping (<) and contiguous (==) ranges
    if (curr.from <= prev.to) {
      // Current range overlaps previous
      return [...acc, { from: prev.from, to: curr.to }];
    }

    // Ranges do not overlap
    return [...acc, prev, curr];
  }, [] as DateRange[]);

  return ret;
}

/**
 * Takes a list of date ranges which may overlap and returns a list of gaps where they are not continuous.
 * If there are no gaps, the returned list will be empty.
 * Date ranges are expected to be `YYYY-MM` from the ISO 8601 date format, where `from` is before or equal to `to`.
 * Invalid date ranges are filtered out of the input and not processed. No error is thrown.
 */
export const findGapsInDateRanges = (dateRanges: DateRange[]): DateRange[] => {
  /* Psuedo code:
    Check all dates are valid and in order
    A gap is found when the `to` of one range is not equal to the `from` of the next range
    If there is a gap, add a new range with `from` being the `to` of the first range and `to` being the `from` of the next range
  */
  const gapRanges: DateRange[] = [];
  
  const sortedDateRanges = mergeOverlappingDateRanges(dateRanges)
    // Sort the date ranges by the `from` date in descending order
    .sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime());
  
  sortedDateRanges.forEach((range: DateRange, index: number) => {
    //console.log('index', index, 'range', range);
    const previousRange = sortedDateRanges[index + 1]; //previous chronologically but next in the array
    //console.log('previousRange', previousRange);
    if (previousRange && previousRange.to < range.from) {
      //console.log('gap found', previousRange.to, range.from);
      gapRanges.push({ from: previousRange.to, to: range.from });
    }
  });
  
  return gapRanges
}

type DatedAddress = { ResidentDates?: { ResidentToGyearMonth?: string | null } | null }

/** DBS-352: an address's 'to' month, or undefined while the applicant has not entered it yet */
const residentTo = (address: DatedAddress): string | undefined => address.ResidentDates?.ResidentToGyearMonth || undefined

/**
 * DBS-352: previous addresses, most recent 'to' month first. Addresses without a 'to' month yet (one just added before
 * any dates were entered) go last, in their current order.
 */
export const sortPreviousAddresses = <T extends DatedAddress>(addresses: T[]): T[] =>
  [...addresses].sort((a, b) => {
    const [toA, toB] = [residentTo(a), residentTo(b)]
    if (toA === undefined || toB === undefined) return (toA === undefined ? 1 : 0) - (toB === undefined ? 1 : 0)
    return toB.localeCompare(toA)
  })

/** DBS-352: the 'to' months of the dated addresses, in order, for comparing orders; undated addresses do not count */
export const previousAddressDateOrder = (addresses: DatedAddress[]): string =>
  addresses.map(residentTo).filter(each => each !== undefined).join(' ')

/**
 * UK postcodes must be in one of the following forms (with one exception, see below): 
 * * A9 9AA 
 * * A99 9AA
 * * AA9 9AA
 * * AA99 9AA
 * * A9A 9AA
 * * AA9A 9AA
 * where A represents an alphabetic character and 9 represents a numeric character.
 *
 * Additional rules apply to alphabetic characters, as follows:
 * * The character in position 1 may not be Q, V or X
 * * The character in position 2 may not be I, J or Z
 * * The character in position 3 may not be I, L, M, N, O, P, Q, R, V, X, Y or Z
 * * The character in position 4 may not be C, D, F, G, I, J, K, L, O, Q, S, T, U or Z
 * * The characters in the rightmost two positions may not be C, I, K, M, O or V
 *
 * The one exception that does not follow these general rules is the postcode “GIR 0AA”, which is a special valid postcode.
 */
export const isValidPostcode = (postcode?: string | null): boolean => {
  if (postcode == null) return false

  const specialPostcode = 'GIR 0AA'
  if (postcode === specialPostcode) return true

  const validPostcodeRegexs = [
    /^[A-Z][0-9] [0-9][A-Z][A-Z]$/,
    /^[A-Z][0-9][0-9] [0-9][A-Z][A-Z]$/,
    /^[A-Z][A-Z][0-9] [0-9][A-Z][A-Z]$/,
    /^[A-Z][A-Z][0-9][0-9] [0-9][A-Z][A-Z]$/,
    /^[A-Z][0-9][A-Z] [0-9][A-Z][A-Z]$/,
    /^[A-Z][A-Z][0-9][A-Z] [0-9][A-Z][A-Z]$/,
  ]
  if (!validPostcodeRegexs.some(regex => regex.test(postcode))) return false

  const invalidFirstPositionChars = 'QVX';
  const invalidSecondPositionChars = 'IJZ';
  const invalidThirdPositionChars = 'ILMNOPQRVXYZ';
  const invalidFourthPositionChars = 'CDFGIJKLOQSTUZ';

  if (invalidFirstPositionChars.includes(postcode[0])) return false
  if (invalidSecondPositionChars.includes(postcode[1])) return false
  if (invalidThirdPositionChars.includes(postcode[2])) return false
  if (invalidFourthPositionChars.includes(postcode[3])) return false
  
  const lastTwoPositionChars = postcode.slice(-2);
  const invalidLastTwoPositionChars = 'CIKMOV';
  if (invalidLastTwoPositionChars.includes(lastTwoPositionChars[0])) return false
  if (invalidLastTwoPositionChars.includes(lastTwoPositionChars[1])) return false

  return true
}
